import React, { useEffect, useState, useRef } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Search, Phone, Mail, MapPin, X, ShieldCheck, Copy, Users as UsersIcon, UserCheck, Trash2, DollarSign, Banknote } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("affiliates"); // 'affiliates' | 'leads'
  const [usersData, setUsersData] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind, id, name, ... }
  const [deleting, setDeleting] = useState(false);
  const debounceRef = useRef(null);

  // Initial load + debounced search across both collections
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
        const [usersResp, leadsResp] = await Promise.all([
          api.get(`/admin/users${params}`),
          api.get(`/admin/leads${params}`),
        ]);
        setUsersData(usersResp.data);
        setLeadsData(leadsResp.data);
        // If user typed a query and matches are stronger on the other tab, auto-switch
        if (q.trim()) {
          if (usersResp.data.count === 0 && leadsResp.data.count > 0) setTab("leads");
          else if (leadsResp.data.count === 0 && usersResp.data.count > 0) setTab("affiliates");
        }
      } catch (e) {
        if (e.response?.status === 403) {
          toast.error("Admin access required");
        } else {
          toast.error("Could not load results");
        }
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const openDetail = async (user_id) => {
    try {
      const { data } = await api.get(`/admin/users/${user_id}`);
      setSelected({ kind: "user", data });
    } catch {
      toast.error("Could not load account");
    }
  };

  const openLead = (lead) => setSelected({ kind: "lead", data: lead });

  const copy = (text, label = "Copied") => copyToClipboard(text, label);

  const reload = async () => {
    const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
    const [usersResp, leadsResp] = await Promise.all([
      api.get(`/admin/users${params}`),
      api.get(`/admin/leads${params}`),
    ]);
    setUsersData(usersResp.data);
    setLeadsData(leadsResp.data);
  };

  const deleteUser = (user_id, name) => {
    setConfirmDelete({ kind: "user", id: user_id, name });
  };

  const deleteLead = (lead_id, name) => {
    setConfirmDelete({ kind: "lead", id: lead_id, name });
  };

  const runConfirmedDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      if (confirmDelete.kind === "user") {
        const { data } = await api.delete(`/admin/users/${confirmDelete.id}`);
        const d = data.deleted;
        toast.success(`Deleted: ${d.links} links · ${d.clicks} clicks · ${d.leads} leads`);
      } else {
        await api.delete(`/admin/leads/${confirmDelete.id}`);
        toast.success("Lead deleted");
      }
      setSelected(null);
      setConfirmDelete(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const usersCount = usersData?.count ?? 0;
  const leadsCount = leadsData?.count ?? 0;

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-[#002FA7] flex items-center gap-2"><ShieldCheck size={14} /> Admin</div>
          <h1 className="text-4xl tracking-tight font-black text-[#111827]">Search affiliates & leads</h1>
          <p className="text-[#4B5563] mt-2">Search by name, email, phone (e.g. <span className="font-mono">6736065</span>), address, or country.</p>
        </div>
      </div>

      <div className="card-flat p-4 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            data-testid="admin-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search anything — phone, email, name, address…"
            className="w-full rounded-md border border-[#E5E7EB] bg-white pl-10 pr-10 py-3 text-sm outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
            autoFocus
          />
          {q && (
            <button onClick={() => setQ("")} data-testid="admin-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827]">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-[#E5E7EB]">
        <TabButton active={tab === "affiliates"} onClick={() => setTab("affiliates")} icon={UsersIcon} count={usersCount} testid="tab-affiliates">
          Affiliates
        </TabButton>
        <TabButton active={tab === "leads"} onClick={() => setTab("leads")} icon={UserCheck} count={leadsCount} testid="tab-leads">
          Leads
        </TabButton>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        {loading && !usersData && !leadsData && (
          <div className="p-12 text-center text-sm text-[#4B5563]">Loading…</div>
        )}
        {tab === "affiliates" && usersData && (
          usersData.users.length === 0 ? (
            <div className="p-12 text-center text-sm text-[#4B5563]" data-testid="admin-no-affiliates">
              {q ? `No affiliates match "${q}".` : "No affiliates yet."}
            </div>
          ) : <AffiliatesTable users={usersData.users} onOpen={openDetail} onCopy={copy} onDelete={deleteUser} currentUserId={user.user_id} />
        )}
        {tab === "leads" && leadsData && (
          leadsData.leads.length === 0 ? (
            <div className="p-12 text-center text-sm text-[#4B5563]" data-testid="admin-no-leads">
              {q ? `No leads match "${q}".` : "No leads yet."}
            </div>
          ) : <LeadsTable leads={leadsData.leads} onOpen={openLead} onCopy={copy} onDelete={deleteLead} />
        )}
      </div>

      {selected?.kind === "user" && <UserDetailDrawer detail={selected.data} onClose={() => setSelected(null)} onCopy={copy} onDelete={deleteUser} currentUserId={user.user_id} />}
      {selected?.kind === "lead" && <LeadDetailDrawer lead={selected.data} onClose={() => setSelected(null)} onCopy={copy} onDelete={deleteLead} />}

      {confirmDelete && (
        <ConfirmDeleteModal
          kind={confirmDelete.kind}
          name={confirmDelete.name}
          deleting={deleting}
          onCancel={() => !deleting && setConfirmDelete(null)}
          onConfirm={runConfirmedDelete}
        />
      )}
    </DashboardLayout>
  );
}

function ConfirmDeleteModal({ kind, name, deleting, onCancel, onConfirm }) {
  const isUser = kind === "user";
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onCancel} data-testid="confirm-delete-overlay" />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="confirm-delete-modal"
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        onClick={onCancel}
      >
        <div
          className="w-full max-w-md rounded-xl bg-white border border-[#E5E7EB] shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{ animation: "fadeUp 0.18s ease-out" }}
        >
          <div className="p-6">
            <div className="h-12 w-12 rounded-full bg-[#FEF2F2] text-[#EF4444] flex items-center justify-center mb-4">
              <Trash2 size={22} />
            </div>
            <div className="font-display font-black text-xl text-[#111827] mb-2">
              {isUser ? "Delete this affiliate?" : "Delete this lead?"}
            </div>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              {isUser
                ? <>You're about to permanently delete <span className="font-bold text-[#111827]">{name}</span> — including every link, click, and lead they've captured. This cannot be undone.</>
                : <>You're about to permanently delete the lead from <span className="font-bold text-[#111827]">{name}</span>. This cannot be undone.</>}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
            <button
              onClick={onCancel}
              disabled={deleting}
              data-testid="confirm-delete-cancel"
              className="inline-flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-bold text-[#111827] hover:border-[#111827] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={deleting}
              data-testid="confirm-delete-submit"
              className="inline-flex items-center gap-2 justify-center rounded-md bg-[#EF4444] px-4 py-2 text-sm font-bold text-white hover:bg-[#DC2626] disabled:opacity-60"
            >
              {deleting ? "Deleting…" : <><Trash2 size={14} /> Delete</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function TabButton({ active, onClick, icon: Icon, count, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${
        active ? "text-[#002FA7] border-[#002FA7]" : "text-[#4B5563] border-transparent hover:text-[#111827]"
      }`}
    >
      <Icon size={14} />
      {children}
      <span className={`text-[11px] tracking-wider uppercase font-bold rounded-full px-2 py-0.5 ${active ? "bg-[#002FA7] text-white" : "bg-[#F3F4F6] text-[#4B5563]"}`}>{count}</span>
    </button>
  );
}

function AffiliatesTable({ users, onOpen, onCopy, onDelete, currentUserId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm" data-testid="admin-users-table">
        <thead>
          <tr className="border-b border-[#E5E7EB] text-[#4B5563] bg-[#F9FAFB]">
            <th className="label-eyebrow font-bold py-3 px-4">Affiliate</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden md:table-cell">Phone</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden lg:table-cell">Address</th>
            <th className="label-eyebrow font-bold py-3 px-4">Links</th>
            <th className="label-eyebrow font-bold py-3 px-4">Clicks</th>
            <th className="label-eyebrow font-bold py-3 px-4">Leads</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden lg:table-cell">Joined</th>
            <th className="label-eyebrow font-bold py-3 px-4 w-12"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.user_id}
              onClick={() => onOpen(u.user_id)}
              data-testid={`admin-user-row-${u.user_id}`}
              className="border-b border-[#F3F4F6] hover:bg-[#EFF6FF] cursor-pointer transition-colors"
            >
              <td className="py-3 px-4">
                <div className="flex items-center gap-3 min-w-0">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="h-9 w-9 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {(u.name || "U").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-bold text-[#111827] truncate flex items-center gap-2">
                      {u.name || "—"}
                      {u.role === "admin" && <span className="text-[10px] tracking-wider uppercase font-bold bg-[#002FA7] text-white px-1.5 py-0.5 rounded">Admin</span>}
                    </div>
                    <div className="text-xs text-[#4B5563] truncate">{u.email}</div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4 hidden md:table-cell font-mono text-xs text-[#4B5563]">
                {u.phone ? (
                  <button onClick={(e) => { e.stopPropagation(); onCopy(u.phone, "Phone copied"); }} className="hover:text-[#002FA7]" title="Click to copy">{u.phone}</button>
                ) : "—"}
              </td>
              <td className="py-3 px-4 hidden lg:table-cell text-[#4B5563] max-w-[220px] truncate">{u.address || "—"}</td>
              <td className="py-3 px-4 font-display font-black">{u.link_count}</td>
              <td className="py-3 px-4 font-display font-black">{u.click_count}</td>
              <td className="py-3 px-4 font-display font-black text-[#002FA7]">{u.lead_count}</td>
              <td className="py-3 px-4 hidden lg:table-cell text-xs text-[#4B5563]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
              <td className="py-3 px-4 text-right">
                {u.user_id !== currentUserId ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(u.user_id, u.name || u.email); }}
                    data-testid={`admin-user-delete-${u.user_id}`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#EF4444] transition-colors"
                    title="Delete user"
                  >
                    <Trash2 size={14} />
                  </button>
                ) : (
                  <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">You</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadsTable({ leads, onOpen, onCopy, onDelete }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm" data-testid="admin-leads-table">
        <thead>
          <tr className="border-b border-[#E5E7EB] text-[#4B5563] bg-[#F9FAFB]">
            <th className="label-eyebrow font-bold py-3 px-4">Lead</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden md:table-cell">Phone</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden md:table-cell">Delivery</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden lg:table-cell">Country</th>
            <th className="label-eyebrow font-bold py-3 px-4">Discount</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden md:table-cell">Affiliate</th>
            <th className="label-eyebrow font-bold py-3 px-4 hidden lg:table-cell">When</th>
            <th className="label-eyebrow font-bold py-3 px-4 w-12"></th>
          </tr>
        </thead>
        <tbody>
          {leads.map((ld) => (
            <tr
              key={ld.lead_id}
              onClick={() => onOpen(ld)}
              data-testid={`admin-lead-row-${ld.lead_id}`}
              className="border-b border-[#F3F4F6] hover:bg-[#EFF6FF] cursor-pointer transition-colors"
            >
              <td className="py-3 px-4">
                <div className="font-bold text-[#111827] truncate">{ld.name}</div>
                <div className="text-xs text-[#4B5563] truncate">{ld.email}</div>
              </td>
              <td className="py-3 px-4 hidden md:table-cell font-mono text-xs text-[#4B5563]">
                {ld.phone ? (
                  <button onClick={(e) => { e.stopPropagation(); onCopy(ld.phone, "Phone copied"); }} className="hover:text-[#002FA7]">{ld.phone}</button>
                ) : "—"}
              </td>
              <td className="py-3 px-4 hidden md:table-cell text-[#4B5563]">{ld.delivery_location}</td>
              <td className="py-3 px-4 hidden lg:table-cell">
                <span className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-0.5 mr-1">{ld.country_code || "??"}</span>
                <span className="text-[#4B5563]">{ld.country || "Unknown"}</span>
              </td>
              <td className="py-3 px-4">
                {ld.wants_discount ? (
                  <span className="inline-flex items-center rounded-full bg-[#002FA7] text-white text-xs font-bold px-2.5 py-0.5">YES</span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-[#F3F4F6] text-[#4B5563] text-xs font-bold px-2.5 py-0.5">NO</span>
                )}
              </td>
              <td className="py-3 px-4 hidden md:table-cell text-xs text-[#4B5563] truncate">{ld.affiliate_name || "—"}</td>
              <td className="py-3 px-4 hidden lg:table-cell text-xs text-[#4B5563]">{new Date(ld.created_at).toLocaleString()}</td>
              <td className="py-3 px-4 text-right">
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(ld.lead_id, ld.name); }}
                  data-testid={`admin-lead-delete-${ld.lead_id}`}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#EF4444] transition-colors"
                  title="Delete lead"
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadDetailDrawer({ lead, onClose, onCopy, onDelete }) {
  const [sales, setSales] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/leads/${lead.lead_id}/sales`);
        if (!cancelled) setSales(data);
      } catch {
        if (!cancelled) setSales([]);
      }
    })();
    return () => { cancelled = true; };
  }, [lead.lead_id]);

  const recordSale = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a sale amount greater than $0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.lead_id}/sales`, {
        amount: amt,
        note: note.trim() || null,
      });
      toast.success(`Sale recorded — $${data.affiliate_commission.toFixed(2)} commission credited` + (data.referrer_commission ? `, $${data.referrer_commission.toFixed(2)} referral bonus` : ""));
      setAmount("");
      setNote("");
      const refreshed = await api.get(`/admin/leads/${lead.lead_id}/sales`);
      setSales(refreshed.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record sale");
    } finally {
      setSaving(false);
    }
  };

  const removeSale = async (sale_id) => {
    try {
      await api.delete(`/admin/sales/${sale_id}`);
      toast.success("Sale removed");
      setSales((prev) => (prev || []).filter((s) => s.sale_id !== sale_id));
    } catch {
      toast.error("Could not remove sale");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-testid="admin-lead-detail-overlay" />
      <aside
        className="fixed top-0 right-0 bottom-0 w-full md:w-[520px] bg-white z-50 flex flex-col shadow-2xl overflow-y-auto"
        data-testid="admin-lead-detail-drawer"
        style={{ animation: "fadeUp 0.18s ease-out" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
          <div className="label-eyebrow text-[#002FA7]">Lead detail</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDelete(lead.lead_id, lead.name)}
              data-testid="admin-lead-detail-delete"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-xs font-bold text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
            >
              <Trash2 size={14} /> Delete lead
            </button>
            <button onClick={onClose} data-testid="admin-lead-detail-close" className="h-9 w-9 rounded-md inline-flex items-center justify-center text-[#4B5563] hover:bg-[#F3F4F6]">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <div className="font-display font-black text-3xl text-[#111827]">{lead.name}</div>
            <div className="text-sm text-[#4B5563] mt-1">Captured {new Date(lead.created_at).toLocaleString()}</div>
            {lead.wants_discount ? (
              <span className="mt-3 inline-flex items-center rounded-full bg-[#002FA7] text-white text-xs font-bold px-3 py-1">Wants discount: YES</span>
            ) : (
              <span className="mt-3 inline-flex items-center rounded-full bg-[#F3F4F6] text-[#4B5563] text-xs font-bold px-3 py-1">Wants discount: NO</span>
            )}
          </div>

          <div className="space-y-3">
            <ContactRow icon={Mail} label="Email" value={lead.email} onCopy={() => onCopy(lead.email, "Email copied")} testid="lead-detail-email" />
            <ContactRow icon={Phone} label="Phone" value={lead.phone || "—"} onCopy={lead.phone ? () => onCopy(lead.phone, "Phone copied") : null} testid="lead-detail-phone" />
            <ContactRow icon={MapPin} label="Delivery location" value={lead.delivery_location} onCopy={() => onCopy(lead.delivery_location, "Address copied")} testid="lead-detail-delivery" />
          </div>

          <div className="rounded-md border border-[#E5E7EB] p-4">
            <div className="label-eyebrow text-[#4B5563] mb-2">Geolocation (from click)</div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-0.5">{lead.country_code || "??"}</span>
              <span className="text-[#111827]">{lead.country || "Unknown"}</span>
            </div>
          </div>

          <div className="rounded-md border border-[#E5E7EB] p-4">
            <div className="label-eyebrow text-[#4B5563] mb-2">Affiliate who captured this lead</div>
            <div className="flex items-center gap-3">
              {lead.affiliate_picture ? (
                <img src={lead.affiliate_picture} alt={lead.affiliate_name} className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold">{(lead.affiliate_name || "U").charAt(0).toUpperCase()}</div>
              )}
              <div className="min-w-0">
                <div className="font-bold text-[#111827] truncate">{lead.affiliate_name || "—"}</div>
                <div className="text-xs text-[#4B5563] truncate">{lead.affiliate_email}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-[#4B5563]">Via link: <span className="text-[#111827] font-bold">{lead.link_name}</span></div>
          </div>

          {/* Sales — record dollar amount, calculate commissions */}
          <div className="rounded-md border-2 border-[#002FA7] bg-[#F8FAFF] p-4" data-testid="admin-sale-section">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={16} className="text-[#002FA7]" />
              <div className="font-display font-extrabold text-[#111827]">Record sale on this lead</div>
            </div>
            <p className="text-xs text-[#4B5563] mb-3">
              Enter the customer's purchase amount. The affiliate who captured this lead gets <span className="font-bold">10%</span> of the sale as commission.
            </p>
            <form onSubmit={recordSale} className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="label-eyebrow text-[#4B5563]">Amount ($)</label>
                  <input
                    data-testid="admin-sale-amount-input"
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="input-base mt-1 font-mono"
                  />
                </div>
                <div className="col-span-2">
                  <label className="label-eyebrow text-[#4B5563]">Note (optional)</label>
                  <input
                    data-testid="admin-sale-note-input"
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Invoice # / SKU / etc."
                    className="input-base mt-1"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-sale-submit"
                className="btn-primary w-full bg-[#002FA7] hover:bg-[#001f70]"
              >
                {saving ? "Recording…" : "Record sale & credit commissions"}
              </button>
            </form>

            {sales && sales.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#DBEAFE]">
                <div className="label-eyebrow text-[#4B5563] mb-2">Recorded sales ({sales.length})</div>
                <div className="space-y-2">
                  {sales.map((s) => (
                    <div key={s.sale_id} className="flex items-start justify-between gap-2 bg-white rounded-md border border-[#E5E7EB] p-2.5 text-xs" data-testid={`admin-sale-item-${s.sale_id}`}>
                      <div className="min-w-0">
                        <div className="font-display font-black text-base text-[#111827]">${s.amount.toFixed(2)}</div>
                        <div className="text-[#4B5563] mt-0.5">
                          Affiliate: <span className="font-bold text-[#111827]">${s.affiliate_commission.toFixed(2)}</span>
                          {s.referrer_commission > 0 && <> · Upline: <span className="font-bold text-[#002FA7]">${s.referrer_commission.toFixed(2)}</span></>}
                        </div>
                        {s.note && <div className="text-[#9CA3AF] mt-0.5 truncate">{s.note}</div>}
                        <div className="text-[#9CA3AF] mt-0.5">{s.recorded_at ? new Date(s.recorded_at).toLocaleString() : ""}</div>
                      </div>
                      <button
                        onClick={() => removeSale(s.sale_id)}
                        data-testid={`admin-sale-delete-${s.sale_id}`}
                        className="text-[#9CA3AF] hover:text-[#EF4444] p-1"
                        title="Remove sale"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-md border border-[#E5E7EB] p-4 text-xs text-[#4B5563] space-y-1">
            <div><span className="label-eyebrow">Lead ID</span> <span className="font-mono ml-2">{lead.lead_id}</span></div>
            <div><span className="label-eyebrow">Click ID</span> <span className="font-mono ml-2">{lead.click_id || "—"}</span></div>
            <div><span className="label-eyebrow">Link ID</span> <span className="font-mono ml-2">{lead.link_id}</span></div>
          </div>
        </div>
      </aside>
    </>
  );
}

function UserDetailDrawer({ detail, onClose, onCopy, onDelete, currentUserId }) {
  const u = detail.user;
  const s = detail.stats;
  const canDelete = u.user_id !== currentUserId;
  const [payouts, setPayouts] = useState([]);
  const [payingOut, setPayingOut] = useState(false);
  const [salesData, setSalesData] = useState(null); // { own_sales, downline_sales }
  const [saleAmount, setSaleAmount] = useState("");
  const [saleNote, setSaleNote] = useState("");
  const [savingSale, setSavingSale] = useState(false);

  const loadSales = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/sales`);
      setSalesData(data);
    } catch {
      setSalesData({ own_sales: [], downline_sales: [] });
    }
  }, [u.user_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${u.user_id}/payouts`);
        if (!cancelled) setPayouts(data);
      } catch {
        // ignore — empty payouts is fine
      }
      if (!cancelled) await loadSales();
    })();
    return () => { cancelled = true; };
  }, [u.user_id, loadSales]);

  const recordSale = async (e) => {
    e.preventDefault();
    const amt = parseFloat(saleAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a sale amount greater than $0");
      return;
    }
    setSavingSale(true);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/sales`, {
        amount: amt,
        note: saleNote.trim() || null,
      });
      toast.success(
        `Sale recorded — $${data.affiliate_commission.toFixed(2)} credited to ${u.name}` +
        (data.referrer_commission ? `, $${data.referrer_commission.toFixed(2)} bonus to upline` : "")
      );
      setSaleAmount("");
      setSaleNote("");
      await loadSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record sale");
    } finally {
      setSavingSale(false);
    }
  };

  const removeSale = async (sale_id) => {
    try {
      await api.delete(`/admin/sales/${sale_id}`);
      toast.success("Sale removed");
      await loadSales();
    } catch {
      toast.error("Could not remove sale");
    }
  };

  const handlePayout = async () => {
    setPayingOut(true);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/payout`);
      toast.success(`Paid $${data.total_amount.toFixed(2)} to ${u.name} (${data.affiliate_sale_count + data.referrer_sale_count} sales)`);
      const refreshed = await api.get(`/admin/users/${u.user_id}/payouts`);
      setPayouts(refreshed.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not pay out");
    } finally {
      setPayingOut(false);
    }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-testid="admin-detail-overlay" />
      <aside
        className="fixed top-0 right-0 bottom-0 w-full md:w-[560px] bg-white z-50 flex flex-col shadow-2xl overflow-y-auto"
        data-testid="admin-detail-drawer"
        style={{ animation: "fadeUp 0.18s ease-out" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
          <div className="label-eyebrow text-[#002FA7]">Affiliate detail</div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePayout}
              disabled={payingOut}
              data-testid="admin-detail-payout"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-xs font-bold text-[#166534] hover:bg-[#F0FDF4] transition-colors disabled:opacity-50"
            >
              <Banknote size={14} /> {payingOut ? "Paying…" : "Pay out"}
            </button>
            {canDelete && (
              <button
                onClick={() => onDelete(u.user_id, u.name || u.email)}
                data-testid="admin-detail-delete-user"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-xs font-bold text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
              >
                <Trash2 size={14} /> Delete account
              </button>
            )}
            <button onClick={onClose} data-testid="admin-detail-close" className="h-9 w-9 rounded-md inline-flex items-center justify-center text-[#4B5563] hover:bg-[#F3F4F6]">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Identity */}
          <div className="flex items-center gap-4">
            {u.picture ? (
              <img src={u.picture} alt={u.name} className="h-20 w-20 rounded-full object-cover border border-[#E5E7EB]" />
            ) : (
              <div className="h-20 w-20 rounded-full bg-[#111827] text-white flex items-center justify-center font-display font-black text-3xl">
                {(u.name || "U").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display font-black text-2xl text-[#111827] truncate flex items-center gap-2">
                {u.name}
                {u.role === "admin" && <span className="text-[10px] tracking-wider uppercase font-bold bg-[#002FA7] text-white px-1.5 py-0.5 rounded">Admin</span>}
              </div>
              <div className="text-sm text-[#4B5563]">via {u.auth_provider === "google" ? "Google" : "Email & password"} · joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
            </div>
          </div>

          {/* Contact info */}
          <div className="space-y-3">
            <ContactRow icon={Mail} label="Email" value={u.email} onCopy={() => onCopy(u.email, "Email copied")} testid="admin-detail-email" />
            <ContactRow icon={Phone} label="Phone" value={u.phone || "—"} onCopy={u.phone ? () => onCopy(u.phone, "Phone copied") : null} testid="admin-detail-phone" />
            <ContactRow icon={MapPin} label="Address" value={u.address || "—"} onCopy={u.address ? () => onCopy(u.address, "Address copied") : null} testid="admin-detail-address" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            <StatBox label="Links" value={s.total_links} />
            <StatBox label="Clicks" value={s.total_clicks} />
            <StatBox label="Unique" value={s.unique_clicks} />
            <StatBox label="Leads" value={s.total_leads} accent />
          </div>

          {/* Record sale on this affiliate */}
          <div className="rounded-md border-2 border-[#002FA7] bg-[#F8FAFF] p-4" data-testid="admin-user-sale-section">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={16} className="text-[#002FA7]" />
              <div className="font-display font-extrabold text-[#111827]">Record sale on this affiliate</div>
            </div>
            <p className="text-xs text-[#4B5563] mb-3">
              Enter how much {u.name?.split(" ")[0] || "this affiliate"} brought into Matrix. They earn <span className="font-bold">10%</span> commission on the amount.
            </p>
            <form onSubmit={recordSale} className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="label-eyebrow text-[#4B5563]">Amount ($)</label>
                  <input
                    data-testid="admin-user-sale-amount-input"
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={saleAmount}
                    onChange={(e) => setSaleAmount(e.target.value)}
                    placeholder="100000"
                    className="input-base mt-1 font-mono"
                  />
                </div>
                <div className="col-span-2">
                  <label className="label-eyebrow text-[#4B5563]">Note (optional)</label>
                  <input
                    data-testid="admin-user-sale-note-input"
                    type="text"
                    value={saleNote}
                    onChange={(e) => setSaleNote(e.target.value)}
                    placeholder="Period / customer / SKU"
                    className="input-base mt-1"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={savingSale}
                data-testid="admin-user-sale-submit"
                className="btn-primary w-full bg-[#002FA7] hover:bg-[#001f70]"
              >
                {savingSale ? "Recording…" : "Record sale & credit commissions"}
              </button>
            </form>

            {salesData && salesData.own_sales.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#DBEAFE]">
                <div className="label-eyebrow text-[#4B5563] mb-2">{u.name?.split(" ")[0] || "Their"}'s sales ({salesData.own_sales.length})</div>
                <div className="space-y-2">
                  {salesData.own_sales.map((sl) => (
                    <div key={sl.sale_id} className="flex items-start justify-between gap-2 bg-white rounded-md border border-[#E5E7EB] p-2.5 text-xs" data-testid={`admin-user-sale-item-${sl.sale_id}`}>
                      <div className="min-w-0">
                        <div className="font-display font-black text-base text-[#111827]">${sl.amount.toFixed(2)}</div>
                        <div className="text-[#4B5563] mt-0.5">
                          Affiliate +${sl.affiliate_commission.toFixed(2)}
                          {sl.referrer_commission > 0 && <> · Upline +${sl.referrer_commission.toFixed(2)}</>}
                          {sl.affiliate_paid_out && <span className="ml-1 text-[#166534] font-bold">(paid)</span>}
                        </div>
                        {sl.note && <div className="text-[#9CA3AF] mt-0.5 italic">"{sl.note}"</div>}
                        <div className="text-[#9CA3AF] mt-0.5">{new Date(sl.recorded_at).toLocaleString()}</div>
                      </div>
                      {!sl.affiliate_paid_out && (
                        <button
                          onClick={() => removeSale(sl.sale_id)}
                          className="text-[#EF4444] hover:bg-[#FEF2F2] p-1 rounded"
                          data-testid={`admin-user-sale-remove-${sl.sale_id}`}
                          title="Remove sale"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {salesData && salesData.downline_sales.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#DBEAFE]">
                <div className="label-eyebrow text-[#4B5563] mb-2">Bonus from downlines ({salesData.downline_sales.length})</div>
                <div className="space-y-2">
                  {salesData.downline_sales.map((sl) => (
                    <div key={sl.sale_id} className="bg-white rounded-md border border-[#E5E7EB] p-2.5 text-xs">
                      <div className="font-display font-black text-base text-[#166534]">+${sl.referrer_commission.toFixed(2)}</div>
                      <div className="text-[#4B5563] mt-0.5">
                        from <span className="font-bold">{sl.downline_name || sl.downline_email}</span>'s ${sl.amount.toFixed(2)} sale
                        {sl.referrer_paid_out && <span className="ml-1 text-[#166534] font-bold">(paid)</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Links */}
          <div>
            <div className="label-eyebrow text-[#4B5563] mb-2">Links ({detail.links.length})</div>
            {detail.links.length === 0 ? (
              <div className="text-sm text-[#4B5563]">This affiliate hasn't created any links yet.</div>
            ) : (
              <div className="space-y-2">
                {detail.links.map((l) => (
                  <div key={l.link_id} className="rounded-md border border-[#E5E7EB] p-3">
                    <div className="font-bold text-sm truncate">{l.name}</div>
                    <div className="font-mono text-xs text-[#4B5563] break-all mt-1">/r/{l.slug}</div>
                    <div className="text-xs text-[#4B5563] mt-1">→ <span className="text-[#111827]">{l.destination_url}</span></div>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span><span className="font-bold text-[#111827]">{l.total_clicks}</span> clicks</span>
                      <span><span className="font-bold text-[#002FA7]">{l.total_leads}</span> leads</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent leads */}
          <div>
            <div className="label-eyebrow text-[#4B5563] mb-2">Recent leads ({detail.recent_leads.length})</div>
            {detail.recent_leads.length === 0 ? (
              <div className="text-sm text-[#4B5563]">No leads captured yet.</div>
            ) : (
              <div className="space-y-2">
                {detail.recent_leads.map((ld) => (
                  <div key={ld.lead_id} className="rounded-md border border-[#E5E7EB] p-3 text-xs">
                    <div className="font-bold text-sm text-[#111827]">{ld.name}</div>
                    <div className="text-[#4B5563]">{ld.email} · <span className="font-mono">{ld.phone || "no phone"}</span></div>
                    <div className="text-[#4B5563]">{ld.delivery_location} · {ld.country || "Unknown"}</div>
                    <div className="text-[#9CA3AF] mt-1">{ld.created_at ? new Date(ld.created_at).toLocaleString() : ""}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payout history */}
          <div>
            <div className="label-eyebrow text-[#4B5563] mb-2 flex items-center gap-2"><Banknote size={14} /> Payout history ({payouts.length})</div>
            {payouts.length === 0 ? (
              <div className="text-sm text-[#4B5563]">No payouts yet — click "Pay out" above to pay pending commissions.</div>
            ) : (
              <div className="space-y-2">
                {payouts.map((p) => (
                  <div key={p.payout_id} className="rounded-md border border-[#BBF7D0] bg-[#F0FDF4] p-3 text-xs" data-testid={`admin-payout-row-${p.payout_id}`}>
                    <div className="font-display font-black text-base text-[#166534]">${p.total_amount.toFixed(2)}</div>
                    <div className="text-[#4B5563]">
                      Own: ${p.affiliate_amount.toFixed(2)} ({p.affiliate_sale_count} sales) · Referral: ${p.referrer_amount.toFixed(2)} ({p.referrer_sale_count} sales)
                    </div>
                    <div className="text-[#9CA3AF] mt-0.5">{new Date(p.paid_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

function ContactRow({ icon: Icon, label, value, onCopy, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5" data-testid={testid}>
      <Icon size={16} className="text-[#4B5563] flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">{label}</div>
        <div className="text-sm text-[#111827] truncate">{value}</div>
      </div>
      {onCopy && (
        <button onClick={onCopy} className="text-[#4B5563] hover:text-[#002FA7]" data-testid={`${testid}-copy`} title="Copy">
          <Copy size={14} />
        </button>
      )}
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] p-3">
      <div className="text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">{label}</div>
      <div className={`font-display font-black text-2xl ${accent ? "text-[#002FA7]" : "text-[#111827]"}`}>{value}</div>
    </div>
  );
}
